import { IonButton, IonDatetime, IonItem, IonLabel, IonText } from "@ionic/react";

type ScheduleFields = {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
};

type AssignmentScheduleFieldsProps = ScheduleFields & {
  onChange: (next: ScheduleFields) => void;
};

const normalizeDateValue = (value?: string | null) => {
  if (!value) {
    return "";
  }
  return value.split("T")[0];
};

const normalizeTimeValue = (value?: string | null) => {
  if (!value) {
    return "";
  }
  const raw = value.includes("T") ? value.split("T")[1] : value;
  return raw.slice(0, 5);
};

const AssignmentScheduleFields: React.FC<AssignmentScheduleFieldsProps> = ({
  startDate,
  startTime,
  endDate,
  endTime,
  onChange,
}) => {
  const updateField = (field: keyof ScheduleFields, value: string) => {
    onChange({
      startDate,
      startTime,
      endDate,
      endTime,
      [field]: value,
    });
  };

  const clearDate = (field: "startDate" | "endDate") => {
    if (field === "startDate") {
      onChange({
        startDate: "",
        startTime: "",
        endDate,
        endTime,
      });
      return;
    }
    onChange({
      startDate,
      startTime,
      endDate: "",
      endTime: "",
    });
  };

  const startDefaultTimeLabel = "00:00";
  const endDefaultTimeLabel = "23:59";

  return (
    <>
      <IonItem lines="full">
        <IonLabel position="stacked">Start date</IonLabel>
        <IonDatetime
          presentation="date"
          value={startDate || undefined}
          onIonChange={(event) => updateField("startDate", normalizeDateValue(event.detail.value as string))}
        />
        {startDate && (
          <IonButton
            slot="end"
            fill="clear"
            size="small"
            onClick={() => clearDate("startDate")}
          >
            Clear
          </IonButton>
        )}
      </IonItem>
      {startDate && (
        <IonItem lines="full">
          <IonLabel position="stacked">Start time (optional)</IonLabel>
          <IonDatetime
            presentation="time"
            value={startTime || startDefaultTimeLabel}
            onIonChange={(event) => updateField("startTime", normalizeTimeValue(event.detail.value as string))}
          />
          {startTime ? (
            <IonButton
              slot="end"
              fill="clear"
              size="small"
              onClick={() => updateField("startTime", "")}
            >
              Clear
            </IonButton>
          ) : (
            <IonText slot="end" color="medium">Default: {startDefaultTimeLabel}</IonText>
          )}
        </IonItem>
      )}
      <IonItem lines="full">
        <IonLabel position="stacked">End date</IonLabel>
        <IonDatetime
          presentation="date"
          value={endDate || undefined}
          onIonChange={(event) => updateField("endDate", normalizeDateValue(event.detail.value as string))}
        />
        {endDate && (
          <IonButton
            slot="end"
            fill="clear"
            size="small"
            onClick={() => clearDate("endDate")}
          >
            Clear
          </IonButton>
        )}
      </IonItem>
      {endDate && (
        <IonItem lines="full">
          <IonLabel position="stacked">End time (optional)</IonLabel>
          <IonDatetime
            presentation="time"
            value={endTime || endDefaultTimeLabel}
            onIonChange={(event) => updateField("endTime", normalizeTimeValue(event.detail.value as string))}
          />
          {endTime ? (
            <IonButton
              slot="end"
              fill="clear"
              size="small"
              onClick={() => updateField("endTime", "")}
            >
              Clear
            </IonButton>
          ) : (
            <IonText slot="end" color="medium">Default: {endDefaultTimeLabel}</IonText>
          )}
        </IonItem>
      )}
    </>
  );
};

export default AssignmentScheduleFields;
